import { EventStatus, FileType, Prisma, ScrapedEventSource } from "@prisma/client";
import { DefaultArgs } from "@prisma/client/runtime/library";
import { invert } from "lodash";
import { simpleActivityValues } from "./activity.types";
import { BadgeTypeItemType } from "./badges.types";
import { TimeFilter } from "./statistics.types";
import { Color } from "./color.types";
import { CustomAppearanceModel, CustomPdfAppearanceSection } from "./custom-appearance.types";
import { EventDate, eventDateValues } from "./events-date.types";
import { CreateFile, fileSelectValues } from "./file.types";
import {
  computablePricingValues,
  editablePricingValues,
  safeComputablePricingValues,
} from "./pricing.types";
import {
  getCustomStructureAppearanceTypeValues,
  getStructureTypeValuesForEvent,
} from "./structures.types";
import { TicketBuyerLimits } from "./ticket-limits.types";
import { TicketTypeItemType } from "./tickets.types";
import { userTypeValues } from "./user.types";

export enum SortBy {
  CLOSEST_DATES = "closest-dates",
  CLOSEST_TO_ME = "closest-to-me",
  RECENTLY_ADDED = "recently-added",
}

export enum Dates {
  DEFAULT = "default",
  TOMORROW = "tomorrow",
  THIS_WEEK = "this-week",
  THIS_WEEKEND = "this-weekend",
  THIS_MONTH = "this-month",
  CUSTOM = "custom",
}

export enum EventTimeFrame {
  UPCOMING = "upcoming",
  PAST = "past",
}

export const eventTypeTypeValues = [
  "cultural",
  "sporting",
  "fair",
  "professionalFair",
  "concert",
  "festival",
  "other",
] as const;

export const scrapedEventSourceValues = [
  "jds",
  "gazetteSalon",
  "foiresInfo",
  "pdf",
  "maFoireMonSalon",
] as const;

export const eventPendingAcceptedAutomaticallyValues = [
  "manuallyAccepted",
  "automaticallyAccepted",
] as const;

export type EventPendingAcceptedAutomaticallyType =
  (typeof eventPendingAcceptedAutomaticallyValues)[number];

export type EventTypeType = (typeof eventTypeTypeValues)[number];

export const isEventTypeType = (str: string): str is EventTypeType => {
  return eventTypeTypeValues.includes(str as EventTypeType);
};

export function getEventTypeTranslation(type: EventTypeType) {
  switch (type) {
    case "cultural":
      return "Culturel";
    case "sporting":
      return "Sportif";
    case "fair":
      return "Foire & salons grand public";
    case "professionalFair":
      return "Salon professionnels";
    case "concert":
      return "Spectacle & concert";
    case "festival":
      return "Festival";
    case "other":
      return "Autre";
  }
}

export function getEventSourceScrapedTranslation(source: ScrapedEventSource) {
  switch (source) {
    case "jds":
      return "JDS";
    case "gazetteSalon":
      return "La Gazette des Salons";
    case "foiresInfo":
      return "Foires Info";
    case "pdf":
      return "PDF LECAM";
    case "maFoireMonSalon":
      return "Ma Foire Mon Salon";
    default:
      return "";
  }
}

export function getEventPendingAcceptedAutomaticallyTranslation(
  eventPending: EventPendingAcceptedAutomaticallyType,
) {
  switch (eventPending) {
    case "manuallyAccepted":
      return "Explicite";
    case "automaticallyAccepted":
      return "Tacite";
    default:
      return eventPending satisfies never;
  }
}

export const eventTagsMapping: Record<string, string> = {
  paid: "payante",
  free: "gratuite",
  indoor: "intérieur",
  outdoor: "extérieur",
} as const;

export const eventTypeMap: Record<EventTypeType, string> = {
  cultural: "Culturel",
  sporting: "Sportif",
  fair: "Foire & salons grand public",
  professionalFair: "Salon professionnels",
  concert: "Spectacle & concert",
  festival: "Festival",
  other: "Autre",
} as const;

export const eventTypeMapReverse = invert(eventTypeMap) as Record<string, EventTypeType>;

export const isEventTypeReverse = (str: string): str is keyof typeof eventTypeMapReverse => {
  return Object.keys(eventTypeMapReverse).includes(str as keyof typeof eventTypeMapReverse);
};

export type Coordinates = {
  lat: number;
  lng: number;
};

export type Bounds = {
  northeast: Coordinates;
  southwest: Coordinates;
};

export type FilterEventType = {
  searchText?: string;
  sortBy?: string;
  dates?: string;
  startDate?: string | null;
  endDate?: string | null;
  places?: string;
  eventTypes?: string[];
  placeTypes?: string[];
  entryFree?: boolean;
  entryPaid?: boolean;
  venueIndoor?: boolean;
  venueOutdoor?: boolean;
  sourceScraped?: ScrapedEventSource[];
  timeFrame?: EventTimeFrame;
  status?: EventStatus[];
  lat?: number;
  long?: number;
  locationDistance?: number;
  isEventAutomaticallyAccepted?: boolean;
  address?: string;
  isFromAi?: boolean;
  isFromVisitor?: boolean;
  zipCode?: string | null;
  city?: string | null;
  country?: string | null;
  organiserId?: string;
  bounds?: Bounds;
  zoomLevel?: number | null;
};

export const eventCreatedByVisitorTypeValues =
  Prisma.validator<Prisma.EventCreatedByVisitorDefaultArgs>()({
    include: {
      createdBy: true,
    },
  });

export type EventCreatedByVisitorType = Prisma.EventCreatedByVisitorGetPayload<
  typeof eventCreatedByVisitorTypeValues
>;

export const eventPicturesTypeValues = Prisma.validator<Prisma.Event$picturesArgs<DefaultArgs>>()({
  select: {
    legend: true,
    order: true,
    file: {
      select: fileSelectValues,
    },
  },
  orderBy: {
    order: "asc",
  },
});

export type EventPictureType = Prisma.EventPictureGetPayload<typeof eventPicturesTypeValues>;

export const eventSummaryTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    address: true,
    image: true,
    pictures: eventPicturesTypeValues,
    ticketTypes: true,
    badgeTypes: true,
    dates: eventDateValues,
    eventStructures: {
      select: {
        structure: {
          select: {
            id: true,
            businessName: true,
            businessSectorRelation: {
              select: {
                id: true,
                name: true,
              },
            },
          },
        },
      },
    },
    activities: {
      select: {
        name: true,
        description: true,
      },
    },
    eventPlace: {
      select: {
        place: {
          select: {
            placeEntrances: {
              select: {
                address: {
                  select: {
                    name: true,
                    lat: true,
                    long: true,
                  },
                },
              },
            },
          },
        },
      },
    },
    organiser: {
      select: {
        admin: {
          select: {
            picture: true,
            firstName: true,
            lastName: true,
            businessName: true,
          },
        },
      },
    },
    ticketing: true,
    customEventAppearance: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
      },
    },
    poster: {
      include: {
        addedTexts: {
          include: {
            style: true,
          },
        },
        logos: {
          include: {
            picture: true,
          },
        },
        pictures: {
          include: {
            picture: true,
          },
        },
      },
    },
  },
  omit: {
    secretKey: true,
  },
});

export type EventSummary = Prisma.EventGetPayload<typeof eventSummaryTypeValues>;

export const eventCardSummaryTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    name: true,
    address: true,
    image: true,
    organiser: {
      select: {
        admin: {
          select: {
            firstName: true,
            lastName: true,
            businessName: true,
          },
        },
      },
    },
    dates: eventDateValues,
  },
});

export type EventCardSummary = Prisma.EventGetPayload<typeof eventCardSummaryTypeValues>;

export const eventDetailsTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    address: true,
    image: true,
    pictures: eventPicturesTypeValues,
    organiser: {
      include: {
        admin: {
          select: {
            picture: true,
            isProfessional: true,
            connectId: true,
            businessName: true,
            firstName: true,
            lastName: true,
            siret: true,
            vat: true,
            email: true,
            phoneNumber: true,
            id: true,
          },
        },
      },
    },
    socials: true,
    dates: eventDateValues,
    eventStructures: {
      select: {
        structureId: true,
        exhibitorProfilesPack: true,
        hall: true,
        stand: true,
        promotionDateRanges: {
          select: {
            from: true,
            to: true,
          },
        },
        structure: {
          select: {
            id: true,
            adminId: true,
            acceptedTerms: true,
            businessName: true,
            businessSectorRelation: {
              select: {
                id: true,
                name: true,
                parentId: true,
              },
            },
            businessEmail: true,
            businessSectorId: true,
            phoneNumber: true,
            description: true,
            admin: {
              select: {
                firstName: true,
                lastName: true,
              },
            },
            customStructureAppearance: getCustomStructureAppearanceTypeValues,
            scannedStructures: {
              select: {
                eventId: true,
              },
            },
            picture: {
              select: fileSelectValues,
            },
            products: {
              select: {
                id: true,
                name: true,
                description: true,
                events: {
                  select: {
                    id: true,
                  },
                },
              },
            },
            activities: {
              select: {
                id: true,
                name: true,
                eventId: true,
                structureSchedule: true,
                ticketTypes: {
                  select: {
                    id: true,
                  },
                },
              },
            },
            commercials: {
              select: {
                user: {
                  select: {
                    firstName: true,
                    lastName: true,
                    email: true,
                  },
                },
              },
            },
          },
        },
      },
    },
    eventPlace: {
      include: {
        areas: true,
        entrances: true,
        place: {
          include: {
            placeEntrances: {
              include: { address: true },
            },
          },
        },
      },
    },
    commercials: {
      select: {
        id: true,
        structure: true,
      },
    },
    ticketing: true,
    ticketTypes: {
      where: {
        deletedAt: null,
        OR: [{ activityId: null }, { activity: { isOnline: true } }],
      },
      include: {
        pricing: computablePricingValues,
        tickets: {
          omit: {
            hmac: true,
          },
        },
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: {
              select: fileSelectValues,
            },
            headerPicture: true,
            textPictures: true,
          },
        },
        templatePdf: true,
        groupLimits: true,
      },
      orderBy: {
        name: "asc",
      },
    },
    badgeTypes: {
      where: {
        deletedAt: null,
        isVisibleOnVisitorTicketing: true,
      },
      include: {
        pricing: computablePricingValues,
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: {
              select: fileSelectValues,
            },
            headerPicture: true,
            textPictures: true,
          },
        },
        templatePdf: true,
      },
      orderBy: {
        name: "asc",
      },
    },
    activities: {
      ...simpleActivityValues,
      where: {
        isOnline: true,
        OR: [
          { activityProgramRequest: { status: "accepted" } },
          { activityProgramRequest: null, structureId: null },
        ],
      },
      include: {
        schedules: {
          include: {
            timetable: true,
          },
          orderBy: {
            timetable: {
              day: "asc",
            },
          },
        },
        activityArea: true,
        ticketTypes: {
          include: {
            pricing: computablePricingValues,
            tickets: {
              omit: {
                hmac: true,
              },
            },
            customAppearance: {
              include: {
                picture: true,
              },
            },
            customPdfAppearance: {
              include: {
                backgroundPicture: {
                  select: fileSelectValues,
                },
                headerPicture: true,
                textPictures: true,
              },
            },
            templatePdf: true,
            groupLimits: true,
          },
        },
        activityProgramRequest: true,
        event: {
          select: {
            name: true,
            ticketing: true,
          },
        },
      },
    },
    eventEntrances: {
      include: {
        address: true,
      },
    },
    customEventAppearance: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
      },
    },
    poster: {
      include: {
        addedTexts: {
          include: {
            style: true,
          },
        },
        logos: {
          include: {
            picture: true,
          },
        },
        pictures: {
          include: {
            picture: true,
          },
        },
      },
    },
    customCalendar: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
      },
    },
  },
  omit: {
    secretKey: true,
  },
});

export type EventDetails = Prisma.EventGetPayload<typeof eventDetailsTypeValues>;

export const eventActivityInclude = Prisma.validator<Prisma.ActivityInclude>()({
  schedules: {
    include: {
      timetable: true,
    },
  },
  activityArea: true,
  ticketTypes: {
    include: {
      pricing: editablePricingValues,
      tickets: {
        include: {
          user: userTypeValues,
        },
        omit: {
          hmac: true,
        },
      },
      customAppearance: {
        include: {
          picture: true,
        },
      },
      customPdfAppearance: {
        include: {
          backgroundPicture: {
            select: fileSelectValues,
          },
          headerPicture: true,
          textPictures: true,
        },
      },
      templatePdf: true,
      groupLimits: true,
      eventGates: true,
    },
  },
  activityProgramRequest: true,
  event: {
    select: {
      name: true,
      ticketing: true,
    },
  },
});

export const eventTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    image: true,
    pictures: eventPicturesTypeValues,
    eventCreatedByVisitor: eventCreatedByVisitorTypeValues,
    ExhibitorAdvancedStatistics: true,
    eventAdditionalInformation: true,
    organiser: {
      select: {
        id: true,
        adminId: true,
        eventCounter: true,
        createdAt: true,
        updatedAt: true,
        exhibitorProfilesPack1Credits: true,
        exhibitorProfilesPack2Credits: true,
        exhibitorProfilesPack3Credits: true,
        aiImageCredits: true,
        admin: {
          include: {
            picture: true,
            roles: {
              select: {
                role: true,
              },
            },
          },
          omit: {
            rightsRoleId: true,
            acExpoRightsRoleId: true,
          },
        },
      },
    },
    socials: true,
    dates: eventDateValues,
    eventStructures: {
      select: {
        id: true,
        eventId: true,
        exhibitorProfilesPack: true,
        structureId: true,
        hall: true,
        stand: true,
        structure: getStructureTypeValuesForEvent(),
        promotionDateRanges: {
          select: {
            from: true,
            to: true,
          },
        },
        hasTicketAlertThreshold: true,
        ticketAlertThreshold: true,
      },
    },
    commercials: {
      select: {
        id: true,
        structure: true,
      },
    },
    eventPlace: {
      include: {
        areas: true,
        entrances: true,
        place: {
          include: {
            placeEntrances: {
              include: {
                address: true,
              },
            },
          },
        },
      },
    },
    address: true,
    eventEntrances: {
      include: {
        address: true,
      },
      orderBy: [
        {
          createdAt: "asc",
        },
        {
          address: {
            address: "asc",
          },
        },
      ],
    },
    activities: {
      where: {
        OR: [
          {
            activityProgramRequest: {
              status: "accepted",
            },
          },
          {
            activityProgramRequest: null,
            structureId: null,
          },
        ],
      },
      include: eventActivityInclude,
    },
    gates: true,
    eventInspectors: {
      include: {
        user: true,
        gate: true,
      },
      orderBy: {
        user: {
          createdAt: "asc",
        },
      },
    },
    ticketTypes: {
      where: {
        deletedAt: null,
      },
      include: {
        pricing: safeComputablePricingValues,
        tickets: {
          omit: {
            hmac: true,
          },
        },
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: {
              select: fileSelectValues,
            },
            headerPicture: true,
            textPictures: true,
          },
        },
        templatePdf: true,
        groupLimits: true,
      },
    },
    badgeTypes: {
      where: {
        deletedAt: null,
      },
      include: {
        pricing: safeComputablePricingValues,
        customAppearance: {
          include: {
            picture: true,
          },
        },
        customPdfAppearance: {
          include: {
            backgroundPicture: {
              select: fileSelectValues,
            },
            headerPicture: true,
            textPictures: true,
          },
        },
        templatePdf: true,
      },
    },
    ticketing: true,
    reports: {
      include: {
        user: {
          include: {
            picture: true,
          },
        },
      },
      where: {
        status: "pending",
      },
    },
    customEventAppearance: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
      },
    },
    bankDetails: {
      include: {
        organiser: true,
        structure: true,
      },
    },
    poster: {
      include: {
        addedTexts: {
          include: {
            style: true,
          },
        },
        logos: {
          include: {
            picture: true,
          },
        },
        pictures: {
          include: {
            picture: true,
          },
        },
      },
    },
    AnalyticsEvent: {
      select: {
        detailsViewCount: true,
      },
    },
    cashRegisters: {
      where: {
        deletedAt: null,
      },
    },
    customCalendar: {
      include: {
        backgroundPicture: {
          select: fileSelectValues,
        },
      },
    },
  },
});

export const eventPosterTypeValues = Prisma.validator<Prisma.CustomPosterDefaultArgs>()({
  include: {
    addedTexts: {
      include: {
        style: {
          select: {
            alignment: true,
            bold: true,
            color: true,
            fontSize: true,
            font: true,
            italic: true,
            strikeThrough: true,
            underline: true,
          },
        },
      },
      omit: {
        id: true,
        customPosterId: true,
      },
    },
    logos: {
      include: {
        picture: {
          select: fileSelectValues,
        },
      },
      omit: {
        id: true,
        customPosterId: true,
        pictureId: true,
      },
    },
    pictures: {
      include: {
        picture: {
          select: fileSelectValues,
        },
      },
      omit: {
        id: true,
        customPosterId: true,
        pictureId: true,
      },
    },
  },
  omit: {
    createdAt: true,
    updatedAt: true,
    eventId: true,
    id: true,
  },
});

export type EventType = Prisma.EventGetPayload<typeof eventTypeValues>;

export type EventPosterType = Prisma.CustomPosterGetPayload<typeof eventPosterTypeValues>;

export type EventInspectorType = EventType["eventInspectors"][number];

export const condensedEventTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    slug: true,
    name: true,
    type: true,
    status: true,
    isPrivate: true,
    isPaidEntry: true,
    isIndoor: true,
    isOutdoor: true,
    password: true,
    organiserName: true,
    organiserEmail: true,
    organiserPhone: true,
    promotionDates: true,
    hasAdvancedStatistics: true,
    hasCustomPdfAppearance: true,
    hasCustomEventAppearance: true,
    hasExportExhibitorsModule: true,
    hasExportProgramModule: true,
    exhibitorPromotionCredits: true,
    openTicketingWhenOnline: true,
    activateBadgesWhenOnline: true,
    setExhibitorsOnlineWhenOnline: true,
    setProgramOnlineWhenOnline: true,
    createdAt: true,
    updatedAt: true,
    image: {
      select: {
        key: true,
        type: true,
      },
    },
    ticketing: {
      select: {
        availabilityStartingDate: true,
        availabilityEndingDate: true,
      },
    },
    organiser: {
      select: {
        admin: {
          select: {
            id: true,
            firstName: true,
            lastName: true,
            phoneNumber: true,
            email: true,
            businessName: true,
          },
        },
      },
    },
    address: {
      select: {
        name: true,
        address: true,
        zipCode: true,
        city: true,
        lat: true,
        long: true,
      },
    },
    eventStructures: {
      select: {
        exhibitorProfilesPack: true,
        structureId: true,
        structure: {
          select: {
            id: true,
            businessName: true,
            businessSectorRelation: {
              select: {
                id: true,
                name: true,
              },
            },
          },
        },
      },
    },
    badgeTypes: {
      select: {
        id: true,
      },
    },
    activities: {
      select: {
        id: true,
      },
    },
    dates: eventDateValues,
    AnalyticsEvent: {
      select: {
        detailsViewCount: true,
      },
    },
    ExhibitorAdvancedStatistics: {
      select: {
        structureId: true,
      },
    },
    poster: {
      include: {
        addedTexts: {
          include: {
            style: true,
          },
        },
        logos: {
          include: {
            picture: true,
          },
        },
        pictures: {
          include: {
            picture: true,
          },
        },
      },
    },
  },
});

export type CondensedEventType = Prisma.EventGetPayload<typeof condensedEventTypeValues>;

export const paginatedUserEventTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    name: true,
    type: true,
    status: true,
    isPrivate: true,
    isPaidEntry: true,
    isIndoor: true,
    isOutdoor: true,
    password: true,
    organiserName: true,
    organiserEmail: true,
    organiserPhone: true,
    promotionDates: true,
    openTicketingWhenOnline: true,
    activateBadgesWhenOnline: true,
    setExhibitorsOnlineWhenOnline: true,
    setProgramOnlineWhenOnline: true,
    image: { select: { key: true, type: true } },
    ticketing: { select: { availabilityStartingDate: true, availabilityEndingDate: true } },
    organiser: { select: { admin: { select: { firstName: true, lastName: true } } } },
    address: { select: { name: true, address: true, zipCode: true, city: true, lat: true, long: true } },
    eventStructures: { select: { id: true } },
    badgeTypes: { select: { id: true } },
    activities: { select: { id: true } },
    dates: eventDateValues,
    AnalyticsEvent: { select: { detailsViewCount: true } },
  },
});

export type PaginatedUserEventType = Prisma.EventGetPayload<typeof paginatedUserEventTypeValues>;

export const userEventTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    ...paginatedUserEventTypeValues.select,
    hasAdvancedStatistics: true,
    hasCustomPdfAppearance: true,
    hasCustomEventAppearance: true,
    hasExportExhibitorsModule: true,
    hasExportProgramModule: true,
    exhibitorPromotionCredits: true,
  },
});

export type UserEventType = Prisma.EventGetPayload<typeof userEventTypeValues>;

export type HomePageEventType = {
  id: string;
  slug: string | null;
  name: string;
  isIndoor: boolean;
  isOutdoor: boolean;
  isPaidEntry: boolean;
  isTicketingAvailable: boolean; // This also checks if there is any ticket type or badge type
  dates: EventDate;
  image?: {
    key: string;
    type: FileType;
  };
  isSponsored: boolean;
  address?: {
    address?: string | null;
    zipCode?: string | null;
    city?: string | null;
    name?: string | null;
    lat?: number | null;
    long?: number | null;
  };
  organiserName: string;
};

export const acExpoEventListTypeValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    image: true,
    organiser: {
      select: {
        admin: {
          select: {
            id: true,
            firstName: true,
            lastName: true,
            phoneNumber: true,
            email: true,
            businessName: true,
          },
        },
      },
    },
    address: true,
    dates: eventDateValues,
    eventCreatedByVisitor: true,
    socials: true,
    eventAdditionalInformation: true,
    poster: {
      include: {
        addedTexts: {
          include: {
            style: true,
          },
        },
        logos: {
          include: {
            picture: true,
          },
        },
        pictures: {
          include: {
            picture: true,
          },
        },
      },
    },
  },
});

export type AcExpoEventListType = Prisma.EventGetPayload<typeof acExpoEventListTypeValues>;

export type MyEventsFilter = Pick<FilterEventType, "searchText" | "status"> & {
  time?: TimeFilter;
};

interface CustomPdfAppearance {
  backgroundColor: string;
  headerColor: string;
  textColor: string;
  xHeaderPicture: number;
  yHeaderPicture: number;
  hHeaderPicture: number;
  wHeaderPicture: number;
  xBackgroundPicture: number;
  yBackgroundPicture: number;
  hBackgroundPicture: number;
  wBackgroundPicture: number;
  backgroundPicture: CreateFile | null;
  headerPicture: CreateFile | null;
  model: CustomAppearanceModel;
  opacity: number;
  pdfSectionsOrder: CustomPdfAppearanceSection[];
  textPictures: CreateFile[];
  customSection1: string | null;
  customSection2: string | null;
}

export type CondensedCustomPdfAppearance = CustomPdfAppearance;

export const defaultCondensedCustomPdfAppearanceValues: CondensedCustomPdfAppearance = {
  backgroundColor: "#FFFFFF",
  headerColor: "#000000",
  textColor: "#000000",
  hBackgroundPicture: 0,
  wBackgroundPicture: 0,
  xBackgroundPicture: 0,
  yBackgroundPicture: 0,
  hHeaderPicture: 0,
  wHeaderPicture: 0,
  xHeaderPicture: 0,
  yHeaderPicture: 0,
  backgroundPicture: null,
  headerPicture: null,
  model: "fourQuarters",
  opacity: 100,
  pdfSectionsOrder: ["qrCode", "customSection1", "customSection2", "conditions"],
  textPictures: [],
  customSection1: null,
  customSection2: null,
};

interface CustomAppearance {
  color: Color;
  picture: CreateFile | null;
}

interface TicketType {
  id: number;
  name: string;
  price: number;
  eventId: string;
  quantity: number;
  customAppearance: CustomAppearance | null;
  customPdfAppearance: CustomPdfAppearance | null;
  templatePdf: CreateFile | null;
}

export type { TicketType };

export const getEventStatusLabel = (status: EventStatus, isAlternative?: boolean) => {
  switch (status) {
    case EventStatus.afterVisitorCreationControl:
      return isAlternative ? "En attente" : "Contrôle création visiteur";
    case EventStatus.visitorCreationRefused:
      return "Création visiteur refusé";
    case EventStatus.draft:
      return "Brouillon";
    case EventStatus.beforeOnlineControl:
      return "Contrôle mise en ligne";
    case EventStatus.online:
      return "En ligne";
    case EventStatus.onlineRefused:
      return isAlternative ? "Refusé" : "Mise en ligne refusé";
    case EventStatus.afterAiCreationControl:
      return isAlternative ? "En attente" : "Contrôle création IA";
    case EventStatus.visitorPendingAcceptation:
    case EventStatus.aiPendingAcceptation:
      return "En cours d'autorisation";
    case EventStatus.aiAccepted:
    case EventStatus.visitorAccepted:
      return "Publication autorisée";
    case EventStatus.duplicateControl:
      return "Potentiel doublon";
  }
};

export type CreateEventFromCsvSchedule = {
  day: Date;
  startHour: string;
  endHour: string;
};

export type CreateEventFromCsvPost = {
  name: string;
  description?: string | null;
  type: EventTypeType;
  isIndoor: boolean;
  isOutdoor: boolean;
  isPaidEntry: boolean;
  place: string;
  schedules: CreateEventFromCsvSchedule[];
  websiteUrl?: string | null;
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  twitterUrl?: string | null;
  youtubeUrl?: string | null;
  instagramUrl?: string | null;
};

export type CreateEventsFromCsvPost = {
  events: CreateEventFromCsvPost[];
};

export type EventCustomFields = {
  startDate: Date;
  endDate: Date;
  organiserName: string | null;
  isTicketingEnabled: boolean;
  isTicketingAvailable: boolean;
  isSponsored: boolean;
  hasPassword?: boolean;
};

export type EventWithCustomField<T> = T & EventCustomFields;

// Public event payload: ticket types are filtered for the caller, the flag is computed before
// that filter so an anonymous visitor knows a Group unlock may reveal Group tickets.
export type PublicEventDetails = EventWithCustomField<EventDetails> & {
  hasGroupTicketTypes: boolean;
  // The caller's ticket budget on this event, so the ticketing caps quantities like the server.
  buyer: TicketBuyerLimits;
};

export const potentialDuplicateEventLeanValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    name: true,
    description: true,
    type: true,
    status: true,
    isIndoor: true,
    isOutdoor: true,
    isPaidEntry: true,
    createdAt: true,
    updatedAt: true,
    image: {
      select: {
        key: true,
        type: true,
      },
    },
    organiser: {
      select: {
        id: true,
        admin: {
          select: {
            id: true,
            firstName: true,
            lastName: true,
            businessName: true,
          },
        },
      },
    },
    address: {
      select: {
        address: true,
        zipCode: true,
        city: true,
        country: true,
        lat: true,
        long: true,
      },
    },
    dates: eventDateValues,
  },
});

export type PotentialDuplicateEventLean = Prisma.EventGetPayload<
  typeof potentialDuplicateEventLeanValues
>;

export const potentialDuplicateEventValues =
  Prisma.validator<Prisma.PotentialDuplicateEventDefaultArgs>()({
    select: {
      score: true,
      event: potentialDuplicateEventLeanValues,
      duplicateEvent: potentialDuplicateEventLeanValues,
    },
  });

export type PotentialDuplicateEvent = Prisma.PotentialDuplicateEventGetPayload<
  typeof potentialDuplicateEventValues
>;

export const eventWithSchedulesValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    dates: eventDateValues,
  },
});

export type EventWithSchedules = Prisma.EventGetPayload<typeof eventWithSchedulesValues>;

export const eventsAccounting = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    name: true,
    organiser: {
      select: {
        id: true,
        admin: {
          select: {
            firstName: true,
            lastName: true,
            connectId: true,
          },
        },
        bankDetails: true,
      },
    },
    eventStructures: {
      select: {
        structure: {
          select: {
            id: true,
            businessName: true,
            bankDetails: true,
            admin: {
              select: {
                firstName: true,
                lastName: true,
                connectId: true,
              },
            },
          },
        },
      },
    },
    dates: eventDateValues,
    bankDetails: true,
  },
});

export type EventAccounting = Prisma.EventGetPayload<typeof eventsAccounting>;

export const duplicateEventValues = Prisma.validator<Prisma.EventDefaultArgs>()({
  include: {
    ticketing: {
      select: {
        id: true,
      },
    },
    eventEntrances: {
      include: {
        address: {
          omit: {
            id: true,
            createdAt: true,
            updatedAt: true,
          },
        },
      },
    },
    image: true,
    pictures: eventPicturesTypeValues,
    socials: {
      omit: {
        id: true,
        createdAt: true,
        updatedAt: true,
      },
    },
    ticketTypes: {
      omit: {
        createdAt: true,
        updatedAt: true,
      },
      include: {
        pricing: editablePricingValues,
        activity: true,
      },
    },
  },
});

export type DuplicatableEvent = Prisma.EventGetPayload<typeof duplicateEventValues>;
export type EventPaidPrestations = {
  name: string;
  date: Date | null;
  unitPrice: number | null;
  unitPriceWithoutTaxes: number | null;
  quantity: number;
  vatRate: number | null;
};

export type EventPrestations = {
  id: string;
  name: string;
  prestations: EventPaidPrestations[];
  taxLessTotal: number;
  total: number;
};

export const eventReportValues = Prisma.validator<Prisma.EventReportDefaultArgs>()({
  include: {
    user: {
      include: {
        picture: true,
      },
    },
  },
});
export type EventReport = Prisma.EventReportGetPayload<typeof eventReportValues>;

export type RemainingReportsCount = {
  remainingReportsCount: number;
};

export const eventReportOptionValues = [
  "fakeEvent",
  "duplicatedEvent",
  "incorrectInformations",
  "other",
] as const;

export type EventReportOption = (typeof eventReportOptionValues)[number];

export type PotentialDuplicatePair = {
  idEvent: string;
  idDuplicate: string;
};

export type AcExpoEventListTypeWithScore = AcExpoEventListType & {
  score: number | string;
};

export type DuplicateEventsByEventId = {
  [key: string]: Array<AcExpoEventListTypeWithScore>;
};

export type VigilEvent = {
  id: string;
  name: string;
  image: EventDetails["image"];
  startDate: string;
  endDate: string;
  organiser: {
    firstName: string;
    lastName: string;
    phone: string;
    connectId: string;
    userId: string;
  };
  secretKey: string;
  contactPhone: string;
  organiserPhone: string;
  organiserEmail: string;
  showTicketNameOnScan: boolean;
};

export type FavoriteUserEvent = {
  id: string;
  userId: string;
  eventId: string;
};

export type EventSuggestionType = {
  value: string;
  label: string;
  organiserEmail?: string;
  organiserPhone?: string;
};

export type EventEntry = (TicketTypeItemType | BadgeTypeItemType) & {
  isBadge: boolean;
};

export type RawEventContextForPoster = {
  name: string;
  type: EventTypeType;
  description: string | null;
  startDate: string;
  endDate: string;
  place: string | null;
  city: string | null;
  country: string | null;
};

export type UpdateEventInterlocutorBody = {
  hasToBeRecontacted?: boolean;
  interlocutorGender?: string | null;
  interlocutorFirstName?: string | null;
  interlocutorLastName?: string | null;
  interlocutorEmail?: string | null;
  interlocutorPhone?: string | null;
  adoptedSolution?: string | null;
  text?: string | null;
};

export type UpdateMaxEntryNotificationsBody = {
  maxEntryNotifications: number;
};
